/**
 * Runtime tenant theming, shared by the patient app and the clinic admin app (M17.3,
 * FR-UI-03, FR-PLAT-03, ADR-036). Moved here from `apps/patient/src/lib/tenant/tenant.ts`.
 *
 * A tenant's brand is **a handful of CSS custom properties on one element** — the
 * contract `packages/ui/README.md` calls the tenant theming contract. Every component in
 * this package compiles to `var(--color-primary)` and friends, so writing the properties
 * is the whole of theming: no provider, no context, no re-render.
 *
 * ## What may be overridden
 *
 * The blue family, the typeface (from a closed list) and the logo. Neutrals, status
 * colours, the type scale, radii and spacing are platform-wide: a clinic brands its
 * accent, not the clinical grammar of the product.
 *
 * ## Stored colours win
 *
 * `primaryHoverColor`, `primarySubtleColor` and `borderColor` are stored on
 * `tenant_branding` (M11.12) so a palette that does not follow the usual lightening curve
 * can be corrected. When one is set and is a hex colour, it is used; when it is absent the
 * value is derived from the primary with `color-mix`, which costs no colour library and
 * follows the primary if anything changes it later.
 *
 * ## The platform's own blue removes the overrides
 *
 * A tenant that carries `#18b3ef` is HeyAllergy (or a clinic that never branded). Its
 * hand-tuned palette in `theme.css` stays in force rather than being replaced by a
 * derivation of itself — which is also why the booking funnel's parity build is
 * unaffected by anything here.
 *
 * Nothing here reads PHI; branding is public configuration.
 */

import { accessiblePalette } from './contrast';

/** The platform's own blue. A tenant that still carries it is not overriding anything. */
export const PLATFORM_PRIMARY = '#18b3ef';

/** `#rgb`, `#rrggbb` or `#rrggbbaa`. Anything else is not painted, ever. */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/* ------------------------------------------------------------------ fonts */

/**
 * The typefaces a tenant may choose (M17.3). A **closed** list, for two reasons:
 *
 *  1. The CSP is `font-src 'self'` on every app, so a webfont from a font CDN would be
 *     refused; only the bundled DM Sans and faces already on the visitor's device can
 *     render.
 *  2. `fontFamily` is written into a CSS custom property. A free-text value would be a
 *     CSS injection surface; a key looked up in this table is not.
 *
 * `value` is what is stored in `tenant_branding.font_family`. The funnel pages that mirror
 * the original site keep their own typeface (`booking.css`, `data-font`), so the choice
 * shows on the design-system screens — sign-in, the portal, the clinic admin.
 */
export interface BrandFont {
  value: string;
  label: string;
  stack: string;
}

export const BRAND_FONTS: readonly BrandFont[] = [
  {
    value: 'DM Sans',
    label: 'DM Sans (platform default)',
    stack: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  {
    value: 'System UI',
    label: 'System UI (the device’s own font)',
    stack: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  {
    value: 'Helvetica',
    label: 'Helvetica / Arial',
    stack: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  {
    value: 'Georgia',
    label: 'Georgia (serif)',
    stack: "Georgia, 'Times New Roman', Times, serif",
  },
];

/**
 * The allowed font a stored value names, or null.
 *
 * Case-insensitive, and a stored stack (`"Georgia, serif"`, written by an older build that
 * took free text) is matched by its first family, so existing rows keep working.
 */
export function resolveBrandFont(value: string | null | undefined): BrandFont | null {
  if (value === null || value === undefined) return null;
  const first = (value.split(',')[0] ?? '').trim().replace(/^['"]|['"]$/g, '');
  const key = first.toLowerCase();
  if (key === '') return null;
  return BRAND_FONTS.find((font) => font.value.toLowerCase() === key) ?? null;
}

/* ------------------------------------------------------------------ URLs */

/**
 * A URL that is about to be pasted into a CSS `url()` or an `href`, checked first: only a
 * same-origin path, an `https:` URL or a `blob:` preview, and no quote, paren, backslash
 * or whitespace that could close the function and start a new declaration.
 */
export function safeAssetUrl(value: string | null | undefined): string | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  const url = value.trim();
  if (/["'()\\\s]/.test(url)) return null;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  if (url.startsWith('blob:')) return url;
  return url.startsWith('https://') ? url : null;
}

/**
 * `url` with `?v=<version>` appended, unless it already carries a `v` parameter
 * (M17.1: the API stamps the public logo and favicon paths itself). The version is the
 * branding row's `updatedAt` epoch, so a save changes the URL and no cache — the
 * browser's, a CDN's, the API's 302 — can serve the old mark under the new payload.
 */
export function versionedAssetUrl(
  url: string | null | undefined,
  version: string | number | null | undefined,
): string | null {
  if (url === null || url === undefined || url === '') return null;
  if (version === null || version === undefined || version === '') return url;
  if (/[?&]v=/.test(url)) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(String(version))}`;
}

/* ------------------------------------------------------------------ theme */

/**
 * The branding fields the theme reads. Structural, so the public payload
 * (`TenantBranding`), the admin's `Branding` and a form draft all fit.
 */
export interface TenantThemeInput {
  primaryColor?: string | null | undefined;
  primaryHoverColor?: string | null | undefined;
  primarySubtleColor?: string | null | undefined;
  borderColor?: string | null | undefined;
  /** Doubles as `--color-primary-deep`, as it did in the patient app since M2.9. */
  accentColor?: string | null | undefined;
  fontFamily?: string | null | undefined;
  logoUrl?: string | null | undefined;
  /** `updatedAt` epoch from the public payload (M17.1). Appended to the logo URL. */
  brandVersion?: string | number | null | undefined;
}

/** Every property `applyTenantBranding` may write, so it can also remove them. */
export const TENANT_THEME_PROPERTIES = [
  '--color-primary',
  '--color-primary-hover',
  '--color-primary-subtle',
  '--color-primary-border',
  '--color-primary-deep',
  '--color-primary-accessible',
  '--color-primary-accessible-hover',
  '--font-sans',
  '--font-display',
  '--logo-url',
] as const;

export type TenantThemeProperty = (typeof TENANT_THEME_PROPERTIES)[number];

function normalizeColor(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim().toLowerCase();
  return HEX_COLOR.test(trimmed) ? trimmed : null;
}

export interface TenantThemeOptions {
  /**
   * Paint the platform's own blue too. Off at runtime (the designed palette stays in
   * force); on in a preview, which must show the colour that is in the box.
   */
  includePlatformPrimary?: boolean;
}

/**
 * The custom properties a brand turns into, as a plain map. Pure: the runtime writes it
 * onto `:root` and a preview spreads it onto a wrapper, so the two cannot disagree.
 */
export function tenantThemeVariables(
  branding: TenantThemeInput,
  options: TenantThemeOptions = {},
): Partial<Record<TenantThemeProperty, string>> {
  const out: Partial<Record<TenantThemeProperty, string>> = {};

  const primary = normalizeColor(branding.primaryColor);
  const overriding =
    primary !== null && (options.includePlatformPrimary === true || primary !== PLATFORM_PRIMARY);

  if (overriding) {
    out['--color-primary'] = primary;
    /*
     * Stored values first (M17.3); `color-mix` when a clinic left one empty.
     *   hover  — a touch darker than the primary
     *   subtle — the tinted surface behind notes and selected options
     *   border — that surface's hairline
     */
    out['--color-primary-hover'] =
      normalizeColor(branding.primaryHoverColor) ?? `color-mix(in srgb, ${primary} 85%, black)`;
    out['--color-primary-subtle'] =
      normalizeColor(branding.primarySubtleColor) ?? `color-mix(in srgb, ${primary} 12%, white)`;
    out['--color-primary-border'] =
      normalizeColor(branding.borderColor) ?? `color-mix(in srgb, ${primary} 32%, white)`;
    out['--color-primary-deep'] =
      normalizeColor(branding.accentColor) ?? `color-mix(in srgb, ${primary} 70%, black)`;

    /*
     * The accessible sibling (Q-P15, KI-03). Every surface that carries white text paints
     * `--color-primary-accessible`; if it stayed at HeyAllergy's #0A6F9A a tenant would get
     * their blue on the textless fills and OUR blue under every label. It is derived rather
     * than stored so a clinic cannot pick an "accessible colour" that is not accessible,
     * and it is returned unchanged when the primary already clears 4.5:1.
     */
    const accessible = accessiblePalette(primary);
    if (accessible !== null) {
      out['--color-primary-accessible'] = accessible.accessible;
      out['--color-primary-accessible-hover'] = accessible.accessibleHover;
    }
  }

  const font = resolveBrandFont(branding.fontFamily);
  if (font !== null) {
    out['--font-sans'] = font.stack;
    out['--font-display'] = font.stack;
  }

  const logo = safeAssetUrl(versionedAssetUrl(branding.logoUrl, branding.brandVersion));
  if (logo !== null) out['--logo-url'] = `url("${logo}")`;

  return out;
}

/**
 * Writes the tenant's brand onto `root` (`:root` by default), and removes every themed
 * property the brand does not set.
 *
 * Idempotent, and safe to call with the platform's own branding: a value that is absent
 * or is the platform default removes the override rather than restating it.
 */
export function applyTenantBranding(branding: TenantThemeInput, root?: HTMLElement): void {
  if (typeof document === 'undefined') return;
  const target = root ?? document.documentElement;
  const variables = tenantThemeVariables(branding);

  for (const name of TENANT_THEME_PROPERTIES) {
    const value = variables[name];
    if (value === undefined) target.style.removeProperty(name);
    else target.style.setProperty(name, value);
  }
}

/* --------------------------------------------------------------- document */

export interface DocumentBrandingInput {
  clinicName: string | null | undefined;
  faviconUrl?: string | null | undefined;
  /** When given, becomes `document.title` and `og:title`. */
  pageTitle?: string | null | undefined;
  brandVersion?: string | number | null | undefined;
}

/** Marks the `<link rel="icon">` this module created, so it only ever removes its own. */
const FAVICON_MARKER = 'data-tenant-favicon';

function upsertMeta(property: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (meta === null) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.append(meta);
  }
  meta.setAttribute('content', content);
}

/**
 * The parts of a brand that live in `<head>` (M17.3): the favicon, and the Open Graph
 * site name and title.
 *
 * - **Favicon.** An existing `<link rel="icon">` is updated; otherwise one is created and
 *   marked. A tenant with no favicon removes only the marked one, so a static icon in
 *   `index.html` is never deleted by a clinic that has none. The URL is versioned.
 * - **Open Graph.** Set at runtime, which is what a person sharing a link from an open tab
 *   gets. Crawlers read the server's HTML and do not run this; server-side injection is a
 *   follow-up (scope design §3.3).
 */
export function applyDocumentBranding(input: DocumentBrandingInput): void {
  if (typeof document === 'undefined') return;

  const favicon = safeAssetUrl(versionedAssetUrl(input.faviconUrl, input.brandVersion));
  const marked = document.head.querySelector<HTMLLinkElement>(`link[${FAVICON_MARKER}]`);
  if (favicon === null) {
    marked?.remove();
  } else {
    let link = marked ?? document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (link === null) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.setAttribute(FAVICON_MARKER, '');
      document.head.append(link);
    }
    link.type = 'image/png';
    link.href = favicon;
  }

  const clinicName = (input.clinicName ?? '').trim();
  const pageTitle = (input.pageTitle ?? '').trim();
  if (clinicName !== '') upsertMeta('og:site_name', clinicName);
  const title = pageTitle === '' ? clinicName : pageTitle;
  if (title !== '') upsertMeta('og:title', title);
  if (pageTitle !== '') document.title = pageTitle;
}
